import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { auth, db, functions, googleProvider } from "./firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser,
  setPersistence,
  browserLocalPersistence,
  signInWithPopup,
  GoogleAuthProvider,
  updateProfile,
} from "firebase/auth";
import {
  doc,
  getDoc,
  onSnapshot,
  updateDoc,
  increment,
  setDoc,
  Timestamp,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
  writeBatch,
} from "firebase/firestore";
import { httpsCallable } from 'firebase/functions';
import { User, UserWithDates } from "@/types/users";
import { validateCsrfToken } from "./csrf";
import { generateVerificationCode } from "@/utils/verificationUtils";
import { logInfo, logError, logWarn } from "@/utils/logUtils";
import { auth as googleOAuthConfig } from '@/config/firebase';

// Import our enhanced auth service functions
import { 
  setAuthCookies, 
  clearAuthCookies, 
  verifyAndRefreshToken, 
  hasValidAuthCookies 
} from "./authService";

interface AuthContextType {
  user: UserWithDates | null;
  firebaseUser: FirebaseUser | null;
  isAdmin: boolean;
  isLoading: boolean;
  needsEmailVerification: boolean;
  signIn: (email: string, password: string) => Promise<{ user: FirebaseUser | null; error: Error | null }>;
  signUp: (email: string, password: string, name?: string) => Promise<{ userId: string | null; error: Error | null }>;
  signInWithGoogle: (mode?: 'signin' | 'signup') => Promise<{ user: FirebaseUser | null; error: Error | null; isExistingUser: boolean }>;
  logout: () => Promise<void>;
  deleteAccount: () => Promise<{ success: boolean; error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Function to detect and merge duplicate user accounts
const detectAndMergeDuplicateUsers = async (email: string, primaryUid: string) => {
  if (!email) return;
  
  try {
    // Find all users with this email
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email));
    const snapshot = await getDocs(q);
    
    if (snapshot.size <= 1) {
      // No duplicates found
      return;
    }
    
    logInfo(`Found ${snapshot.size} accounts with the same email: ${email}`);
    
    const batch = writeBatch(db);
    const primaryUserRef = doc(db, 'users', primaryUid);
    let primaryUserData: any = null;
    
    // Collect all user data for merging
    for (const docSnapshot of snapshot.docs) {
      const userData = docSnapshot.data();
      
      if (docSnapshot.id === primaryUid) {
        primaryUserData = userData;
        continue;
      }
      
      // Mark the duplicate for deletion
      const duplicateRef = doc(db, 'users', docSnapshot.id);
      batch.delete(duplicateRef);
    }
    
    if (primaryUserData) {
      // Update the login count on the primary user
      batch.update(primaryUserRef, {
        loginCount: increment(snapshot.size - 1),
        updatedAt: serverTimestamp()
      });
    }
    
    await batch.commit();
    logInfo(`Merged duplicate accounts for email: ${email}`);
  } catch (error) {
    logError('Error merging duplicate accounts:', error);
  }
};

// Function to generate expiry date 24 hours from now
const generateExpiryDate = (hours: number = 24) => {
  const now = new Date();
  return new Date(now.getTime() + hours * 60 * 60 * 1000);
};

const convertTimestampsToDates = (userData: User, docId: string): UserWithDates => {
  const { lastActive, createdAt, updatedAt, verificationData, termsAcceptedAt, ...rest } = userData;
  
  const result: UserWithDates = {
    ...rest,
    lastActive: lastActive?.toDate() || new Date(),
    createdAt: createdAt?.toDate() || new Date(),
    updatedAt: updatedAt?.toDate() || new Date(),
    termsAcceptedAt: termsAcceptedAt?.toDate() || null,
    id: docId,
    uid: docId
  };
  
  // Convert verification data timestamps if they exist
  if (verificationData) {
    result.verificationData = {
      ...verificationData,
      createdAt: verificationData.createdAt?.toDate() || new Date(),
      expiresAt: verificationData.expiresAt?.toDate() || generateExpiryDate()
    };
  }
  
  return result;
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserWithDates | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [needsEmailVerification, setNeedsEmailVerification] = useState(false);
  
  // Add refs to track in-progress operations
  const isSigningUpRef = useRef(false);
  const isGoogleSigningInRef = useRef(false);

  // Handle user document updates
  const handleUserDocUpdate = useCallback(async (currentUser: FirebaseUser | null) => {
    if (!currentUser) {
      setUser(null);
      setIsAdmin(false);
      setNeedsEmailVerification(false);
      // Clear cookies when user is null
      clearAuthCookies();
      return;
    }

    try {
      // Set auth cookies whenever user data is updated
      await setAuthCookies(currentUser);
      
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data() as User;
        logInfo('User data loaded:', { email: userData.email, role: userData.role });
        const userWithDates = convertTimestampsToDates(userData, currentUser.uid);
        setUser(userWithDates);
        setIsAdmin(userData.role === 'admin' || userData.isAdmin === true);
        
        // Check if email verification is needed - skip for Google-authenticated users
        const isGoogleUser = currentUser.providerData.some(provider => provider.providerId === 'google.com');
        setNeedsEmailVerification(!userData.isEmailVerified && userData.accountStatus === 'pending' && !isGoogleUser);
      } else {
        logInfo('Creating new user document');
        // Create verification data for new users - skip for Google-authenticated users
        const isGoogleUser = currentUser.providerData.some(provider => provider.providerId === 'google.com');
        const verificationCode = isGoogleUser ? null : generateVerificationCode();
        const now = new Date();
        const expiryDate = generateExpiryDate();
        
        const newUser: User = {
          email: currentUser.email || '',
          name: currentUser.displayName || '',
          role: 'customer',
          isAdmin: false,
          accountStatus: isGoogleUser ? 'active' : 'pending',
          isEmailVerified: isGoogleUser ? true : currentUser.emailVerified,
          verificationData: isGoogleUser ? null : {
            code: verificationCode,
            createdAt: Timestamp.fromDate(now),
            expiresAt: Timestamp.fromDate(expiryDate),
            attempts: 0
          },
          loginCount: 1,
          lastActive: Timestamp.now(),
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          lastLogin: Timestamp.now(),
          termsAcceptedAt: isGoogleUser ? Timestamp.now() : null,
          photoURL: currentUser.photoURL || null
        };
        await setDoc(userRef, newUser);
        
        const userWithDates = convertTimestampsToDates(newUser, currentUser.uid);
        setUser(userWithDates);
        setIsAdmin(false);
        setNeedsEmailVerification(!isGoogleUser && !currentUser.emailVerified);
      }
    } catch (error) {
      logError('Error handling user document:', error);
      setUser(null);
      setIsAdmin(false);
      setNeedsEmailVerification(false);
    }
  }, []);

  // Set up auth state listener with enhanced cookie handling
  useEffect(() => {
    // Track if components are mounted
    let isMounted = true;
    let userDocUnsubscribe: (() => void) | null = null;

    // Attempt to restore session from cookies first
    if (hasValidAuthCookies()) {
      setIsLoading(true); // Keep loading until Firebase confirms
    }

    // Set persistence immediately when auth component loads
    setPersistence(auth, browserLocalPersistence)
      .then(() => {
        logInfo('Browser persistence set successfully');
      })
      .catch((error) => {
        logError('Error setting persistence:', error);
      });

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      // Guard against state updates after unmount
      if (!isMounted) return;
      
      logInfo('Auth state changed:', currentUser?.email);
      
      try {
        setFirebaseUser(currentUser);

        if (currentUser) {
          // Ensure cookies are set/refreshed when auth state changes
          await setAuthCookies(currentUser);
          
          // Initial load of user data
          await handleUserDocUpdate(currentUser);
          
          // Set up real-time listener for user document updates
          const userRef = doc(db, 'users', currentUser.uid);
          userDocUnsubscribe = onSnapshot(userRef, (doc) => {
            if (!isMounted) return;
            
            if (doc.exists()) {
              const userData = doc.data() as User;
              logInfo('User doc updated:', { role: userData.role, isEmailVerified: userData.isEmailVerified });
              const userWithDates = convertTimestampsToDates(userData, currentUser.uid);
              setUser(userWithDates);
              setIsAdmin(userData.role === 'admin' || userData.isAdmin === true);
              setNeedsEmailVerification(!userData.isEmailVerified && userData.accountStatus === 'pending');
            }
          }, (error) => {
            logError('Error in user document listener:', error);
          });
        } else {
          if (userDocUnsubscribe) {
            userDocUnsubscribe();
            userDocUnsubscribe = null;
          }
          
          // Clear cookies when logged out
          clearAuthCookies();
          
          setUser(null);
          setIsAdmin(false);
          setNeedsEmailVerification(false);
        }
        
        setIsLoading(false);
      } catch (error) {
        logError('Auth state change error:', error);
        setIsLoading(false);
      }
    }, (error) => {
      logError('Auth state change error:', error);
      setIsLoading(false);
    });

    // Set up a token refresh interval to keep cookies fresh
    const tokenRefreshInterval = setInterval(() => {
      verifyAndRefreshToken().catch(error => {
        logError('Token refresh error:', error);
      });
    }, 10 * 60 * 1000); // Check every 10 minutes

    return () => {
      unsubscribe();
      if (userDocUnsubscribe) {
        userDocUnsubscribe();
      }
      clearInterval(tokenRefreshInterval);
      isMounted = false;
    };
  }, [handleUserDocUpdate]);

  const createOrUpdateUserDoc = async (firebaseUser: FirebaseUser) => {
    try {
      const userRef = doc(db, 'users', firebaseUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('email', '==', firebaseUser.email));
        const existingUsers = await getDocs(q);
        
        if (!existingUsers.empty) {
          console.log('User with this email already exists in Firestore');
          const existingUser = existingUsers.docs[0];
          
          if (existingUser.id !== firebaseUser.uid) {
            const batch = writeBatch(db);
            
            // Get existing data but remove id and uid fields
            const existingData = existingUser.data();
            // Remove any id or uid properties if they exist
            const { id, uid, ...cleanedData } = existingData as any;
            
            batch.delete(existingUser.ref);
            batch.set(userRef, {
              ...cleanedData,
              lastLogin: serverTimestamp(),
              loginCount: increment(1),
              updatedAt: serverTimestamp()
            });
            await batch.commit();
          }
        } else {
          // Create verification data for new users
          const verificationCode = generateVerificationCode();
          const now = new Date();
          const expiryDate = generateExpiryDate();
          
          await setDoc(userRef, {
            email: firebaseUser.email,
            name: firebaseUser.displayName || '',
            role: 'customer',
            isAdmin: false,
            accountStatus: 'pending', // Users start with pending status
            isEmailVerified: firebaseUser.emailVerified,
            verificationData: {
              code: verificationCode,
              createdAt: Timestamp.fromDate(now),
              expiresAt: Timestamp.fromDate(expiryDate),
              attempts: 0
            },
            loginCount: 1,
            lastActive: serverTimestamp(),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            lastLogin: serverTimestamp()
          });
        }
      } else {
        await updateDoc(userRef, {
          lastActive: serverTimestamp(),
          lastLogin: serverTimestamp(),
          loginCount: increment(1),
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error creating/updating user document:', error);
      throw error;
    }
  };

  const getFriendlyErrorMessage = (error: any) => {
    const errorCode = error?.code || error?.message || '';
    
    switch (errorCode) {
      case 'auth/invalid-credential':
        return 'Invalid email or password. Please check your credentials and try again.';
      case 'auth/user-not-found':
        return 'No account found with this email. Please check your email or sign up.';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again.';
      case 'auth/email-already-in-use':
        return 'An account with this email already exists. Please log in instead.';
      case 'auth/weak-password':
        return 'Password is too weak. Please use at least 6 characters.';
      case 'auth/invalid-email':
        return 'Invalid email address. Please enter a valid email.';
      case 'auth/user-disabled':
        return 'This account has been disabled. Please contact support.';
      case 'auth/too-many-requests':
        return 'Too many unsuccessful attempts. Please try again later or reset your password.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your internet connection and try again.';
      case 'auth/popup-closed-by-user':
        return 'Authentication window was closed. Please try again.';
      default:
        if (errorCode.includes('auth/')) {
          return 'Authentication error. Please try again.';
        }
        return 'An unexpected error occurred. Please try again.';
    }
  };

  const signIn = useCallback(async (email: string, password: string): Promise<{ user: FirebaseUser | null; error: Error | null }> => {
    try {
      // Set browser persistence
      await setPersistence(auth, browserLocalPersistence);
      
      // Perform sign in
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Update user document
      await createOrUpdateUserDoc(userCredential.user);
      
      // Set auth cookies after successful sign in
      await setAuthCookies(userCredential.user);
      
      return { user: userCredential.user, error: null };
    } catch (error: any) {
      console.error("Sign in error:", error);
      return { user: null, error: new Error(getFriendlyErrorMessage(error)) };
    }
  }, []);

  const signUp = useCallback(async (email: string, password: string, name?: string): Promise<{ userId: string | null; error: Error | null }> => {
    // Prevent duplicate signUp calls
    if (isSigningUpRef.current) {
      return { userId: null, error: new Error("A signup request is already in progress") };
    }
    
    isSigningUpRef.current = true;
    
    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Set the display name if provided
      if (name) {
        await updateProfile(userCredential.user, {
          displayName: name
        });
      }
      
      // Update user document
      await createOrUpdateUserDoc(userCredential.user);
      
      return { userId: userCredential.user.uid, error: null };
    } catch (error: any) {
      console.error("Sign up error:", error);
      return { userId: null, error: new Error(getFriendlyErrorMessage(error)) };
    } finally {
      isSigningUpRef.current = false;
    }
  }, []);

  const handleGoogleSignIn = async (mode: 'signin' | 'signup' = 'signin'): Promise<{ user: FirebaseUser | null; error: Error | null; isExistingUser: boolean }> => {
    // Prevent duplicate Google sign-in attempts
    if (isGoogleSigningInRef.current) {
      return { 
        user: null, 
        error: new Error("A Google sign-in request is already in progress"),
        isExistingUser: false 
      };
    }
    
    isGoogleSigningInRef.current = true;
    
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('https://www.googleapis.com/auth/userinfo.profile');
      provider.addScope('https://www.googleapis.com/auth/userinfo.email');
      
      provider.setCustomParameters({
        prompt: 'select_account'
      });

      // Pre-check for existing account with the same email before full authentication
      // This avoids the redirect flow issues if we're in signup mode
      if (mode === 'signup') {
        try {
          // Get credential without signing in
          const credential = await signInWithPopup(auth, provider);
          const email = credential.user?.email;

          if (email) {
            // Check if a user with this email already exists in Firestore
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('email', '==', email));
            const existingUsers = await getDocs(q);
            
            if (!existingUsers.empty) {
              // User exists - sign out immediately before any auth state listeners fire
              await firebaseSignOut(auth);
              return { user: null, error: null, isExistingUser: true };
            }

            // No existing user, continue with normal flow
            const user = credential.user;
            const userRef = doc(db, 'users', user.uid);
            
            // Create new user document for first-time Google sign-in
            await setDoc(userRef, {
              email: user.email,
              name: user.displayName || '',
              phoneNumber: null,
              role: 'customer',
              isAdmin: false,
              accountStatus: 'active',
              isEmailVerified: true,
              verificationData: null,
              loginCount: 1,
              lastActive: serverTimestamp(),
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              lastLogin: serverTimestamp(),
              termsAcceptedAt: serverTimestamp(),
              photoURL: user.photoURL || null
            });

            // Clean up any duplicate users with the same email
            await detectAndMergeDuplicateUsers(email, user.uid);
            
            return { user, error: null, isExistingUser: false };
          }
        } catch (error) {
          console.error('Error checking existing account:', error);
          // Continue with normal flow if pre-check fails
        }
      }

      // Normal signin flow
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      if (user) {
        // Check if the user already exists in firestore 
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        const userExists = userDoc.exists();

        // Only for signin mode - user document update
        if (!userExists) {
          // Create new user document for first-time Google sign-in
          await setDoc(userRef, {
            email: user.email,
            name: user.displayName || '',
            phoneNumber: null,
            role: 'customer',
            isAdmin: false,
            accountStatus: 'active',
            isEmailVerified: true,
            verificationData: null,
            loginCount: 1,
            lastActive: serverTimestamp(),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            lastLogin: serverTimestamp(),
            termsAcceptedAt: serverTimestamp(),
            photoURL: user.photoURL || null
          });
        } else {
          // Update existing user document
          await updateDoc(userRef, {
            lastActive: serverTimestamp(),
            lastLogin: serverTimestamp(),
            loginCount: increment(1),
            updatedAt: serverTimestamp(),
            isEmailVerified: true,
            accountStatus: 'active',
            photoURL: user.photoURL || null
          });
        }

        // Clean up any duplicate users with the same email
        if (user.email) {
          await detectAndMergeDuplicateUsers(user.email, user.uid);
        }

        return { user, error: null, isExistingUser: userExists };
      }

      return { 
        user: null, 
        error: new Error('No user data received from Google'),
        isExistingUser: false 
      };
    } catch (error) {
      // Handle popup closed by user separately to avoid showing error message
      if (error instanceof Error && error.message.includes('auth/popup-closed-by-user')) {
        return { 
          user: null, 
          error: null,
          isExistingUser: false 
        };
      }
      
      console.error('Google sign in error:', error);
      return { 
        user: null, 
        error: error instanceof Error ? error : new Error('Unknown error occurred'),
        isExistingUser: false 
      };
    } finally {
      isGoogleSigningInRef.current = false;
    }
  };

  const logout = async () => {
    try {
      if (user && firebaseUser) {
        const userRef = doc(db, "users", firebaseUser.uid);
        await updateDoc(userRef, {
          lastActive: serverTimestamp(),
        });
      }
      
      // Clear cookies before Firebase logout
      clearAuthCookies();
      
      await firebaseSignOut(auth);
    } catch (error) {
      logError('Error during logout:', error);
      // Force cookie cleanup even if Firebase logout fails
      clearAuthCookies();
    }
  };

  const deleteAccount = async () => {
    try {
      if (!firebaseUser || !user) {
        return { success: false, error: new Error("No authenticated user") };
      }

      const { deleteUserAccount } = await import('./firestore');
      
      await deleteUserAccount(firebaseUser.uid);
      
      await firebaseUser.delete();
      
      await logout();
      
      return { success: true, error: null };
    } catch (error) {
      console.error("Error deleting account:", error);
      return { success: false, error: error as Error };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        firebaseUser,
        isAdmin,
        isLoading,
        needsEmailVerification,
        signIn,
        signUp,
        signInWithGoogle: handleGoogleSignIn,
        logout,
        deleteAccount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
